/**
 * GLOBAL COMMAND PALETTE (⌘K / Ctrl+K)
 *
 * A lightweight spotlight-style launcher. Zero third-party deps — it
 * uses a home-grown subsequence matcher with a handful of scoring rules
 * that are plenty good for "find a page / entity fast".
 *
 * Data sources:
 *   1. Static routes (`ROUTES` below) — always searchable
 *   2. Recently viewed items (via useRecentlyViewed) — ranked above
 *      static routes so "jump back to the last job I looked at" works
 *   3. Custom commands registered via `registerCommand` (reserved for
 *      future in-app actions)
 *
 * Keybinding:
 *   ⌘K / Ctrl+K toggles open
 *   Esc closes
 *   ↑/↓ navigate, Enter activates
 */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecentlyViewed } from '../recent/useRecentlyViewed';

// ---------------------------------------------------------------------------
// Route manifest. Curated subset — only the destinations a power user
// actually wants to jump to. If a path 404s for a given user (e.g. admin
// pages for non-admins), `requires` gates it.
// ---------------------------------------------------------------------------
const ROUTES = [
  // Top-level
  { id: 'home', label: 'Home', to: '/', keywords: 'dashboard landing', category: 'Navigate' },
  { id: 'jobs', label: 'Browse Jobs', to: '/jobs', keywords: 'find work opportunities', category: 'Navigate' },
  { id: 'talent', label: 'Find Talent', to: '/talent', keywords: 'freelancers hire', category: 'Navigate' },
  { id: 'services', label: 'Services', to: '/services', keywords: 'categories', category: 'Navigate' },
  // Work
  { id: 'post-job', label: 'Post a Job', to: '/post-job', keywords: 'new job create', category: 'Actions', requires: 'auth' },
  { id: 'my-jobs', label: 'My Jobs', to: '/my-jobs', keywords: 'client jobs', category: 'Work', requires: 'auth' },
  { id: 'proposals', label: 'Proposals', to: '/proposals', keywords: 'applications', category: 'Work', requires: 'auth' },
  { id: 'contracts', label: 'Contracts', to: '/contracts', keywords: 'agreements', category: 'Work', requires: 'auth' },
  { id: 'deliverables', label: 'Deliverables', to: '/deliverables', keywords: 'milestones', category: 'Work', requires: 'auth' },
  { id: 'messages', label: 'Messages', to: '/messages', keywords: 'chat inbox conversations', category: 'Work', requires: 'auth' },
  // Money
  { id: 'wallet', label: 'Wallet', to: '/wallet', keywords: 'earnings payments', category: 'Money', requires: 'auth' },
  { id: 'escrow', label: 'Escrow', to: '/escrow', keywords: 'funds held', category: 'Money', requires: 'auth' },
  // Settings
  { id: 'settings', label: 'Settings', to: '/settings', keywords: 'account profile', category: 'Settings', requires: 'auth' },
  { id: 'security', label: 'Security', to: '/security', keywords: '2fa password', category: 'Settings', requires: 'auth' },
  { id: 'notification-prefs', label: 'Notification Preferences', to: '/notification-preferences', keywords: 'alerts email', category: 'Settings', requires: 'auth' },
  // Admin
  { id: 'admin', label: 'Admin Dashboard', to: '/admin', keywords: 'moderation users', category: 'Admin', requires: 'admin' },
  // Resources
  { id: 'help', label: 'Help Center', to: '/help', keywords: 'support faq', category: 'Help' },
  { id: 'how-it-works', label: 'How It Works', to: '/how-it-works', keywords: 'guide tour', category: 'Help' },
  { id: 'pricing', label: 'Pricing', to: '/pricing', keywords: 'plans cost', category: 'Help' },
];

// ---------------------------------------------------------------------------
// Command registration (extension point)
// ---------------------------------------------------------------------------
const CommandContext = createContext({
  open: () => {},
  close: () => {},
  registerCommand: () => () => {},
});

export const useCommandPalette = () => useContext(CommandContext);

// ---------------------------------------------------------------------------
// Scoring: subsequence match + prefix bonus
// ---------------------------------------------------------------------------
const scoreMatch = (haystack, needle) => {
  if (!needle) return { score: 1, indices: [] };
  const h = haystack.toLowerCase();
  const n = needle.toLowerCase();
  if (h.includes(n)) {
    const idx = h.indexOf(n);
    return {
      score: 100 - idx + (idx === 0 ? 50 : 0),
      indices: Array.from({ length: n.length }, (_, i) => idx + i),
    };
  }
  // Subsequence match
  let hi = 0;
  let score = 0;
  const indices = [];
  for (let ni = 0; ni < n.length; ni += 1) {
    let found = -1;
    for (; hi < h.length; hi += 1) {
      if (h[hi] === n[ni]) {
        found = hi;
        hi += 1;
        break;
      }
    }
    if (found === -1) return null;
    indices.push(found);
    score += 10 - Math.min(9, found);
  }
  return { score, indices };
};

// ---------------------------------------------------------------------------
// Palette component
// ---------------------------------------------------------------------------
export const CommandPaletteProvider = ({
  children,
  isLoggedIn = false,
  userType = '',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();

  const recentJobs = useRecentlyViewed('job');
  const recentFreelancers = useRecentlyViewed('freelancer');
  const recentGigs = useRecentlyViewed('gig');

  const openPalette = useCallback(() => {
    setQuery('');
    setActiveIdx(0);
    setOpen(true);
  }, []);

  const closePalette = useCallback(() => setOpen(false), []);

  // Global keybinding
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((v) => !v);
        setQuery('');
        setActiveIdx(0);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Focus the input when opened.
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  // Build the search pool.
  const pool = useMemo(() => {
    const items = [];

    recentJobs.forEach((j) =>
      items.push({
        id: `recent-job-${j.id}`,
        label: j.title || `Job ${j.id}`,
        subtitle: 'Recently viewed job',
        to: `/jobs/${j.id}`,
        category: 'Recent',
        priority: 3,
      })
    );
    recentFreelancers.forEach((f) =>
      items.push({
        id: `recent-freelancer-${f.id}`,
        label: f.name || f.title || `Freelancer ${f.id}`,
        subtitle: 'Recently viewed freelancer',
        to: `/freelancers/${f.id}`,
        category: 'Recent',
        priority: 3,
      })
    );
    recentGigs.forEach((g) =>
      items.push({
        id: `recent-gig-${g.id}`,
        label: g.title || `Gig ${g.id}`,
        subtitle: 'Recently viewed gig',
        to: `/gigs/${g.id}`,
        category: 'Recent',
        priority: 3,
      })
    );

    ROUTES.forEach((r) => {
      if (r.requires === 'auth' && !isLoggedIn) return;
      if (r.requires === 'admin' && userType !== 'admin') return;
      items.push({
        id: r.id,
        label: r.label,
        subtitle: r.keywords,
        to: r.to,
        category: r.category,
        priority: 1,
      });
    });

    return items;
  }, [recentJobs, recentFreelancers, recentGigs, isLoggedIn, userType]);

  // Filter + score.
  const filtered = useMemo(() => {
    if (!query.trim()) {
      // Default: show recents first, then curated nav.
      return pool
        .slice()
        .sort((a, b) => b.priority - a.priority)
        .slice(0, 20);
    }
    const scored = [];
    pool.forEach((item) => {
      const haystack = `${item.label} ${item.subtitle || ''}`;
      const match = scoreMatch(haystack, query.trim());
      if (match) {
        scored.push({
          ...item,
          _score: match.score + (item.priority || 0) * 5,
        });
      }
    });
    scored.sort((a, b) => b._score - a._score);
    return scored.slice(0, 20);
  }, [pool, query]);

  // Reset active index when results shrink.
  useEffect(() => {
    if (activeIdx >= filtered.length) setActiveIdx(0);
  }, [filtered.length, activeIdx]);

  const activate = useCallback(
    (item) => {
      if (!item) return;
      setOpen(false);
      if (item.to) navigate(item.to);
      if (typeof item.run === 'function') item.run();
    },
    [navigate]
  );

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      activate(filtered[activeIdx]);
    }
  };

  // Scroll active row into view.
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(
      `[data-cmd-idx="${activeIdx}"]`
    );
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [activeIdx, filtered]);

  const ctxValue = useMemo(
    () => ({
      open: openPalette,
      close: closePalette,
      registerCommand: () => () => {}, // reserved
    }),
    [openPalette, closePalette]
  );

  // Group by category for display
  const grouped = useMemo(() => {
    const byCat = new Map();
    filtered.forEach((item, idx) => {
      const cat = item.category || 'Other';
      if (!byCat.has(cat)) byCat.set(cat, []);
      byCat.get(cat).push({ ...item, _idx: idx });
    });
    return [...byCat.entries()];
  }, [filtered]);

  return (
    <CommandContext.Provider value={ctxValue}>
      {children}
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Command palette"
          className="fixed inset-0 z-[100] bg-slate-900/50 backdrop-blur-sm flex items-start justify-center pt-[15vh] px-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closePalette();
          }}
        >
          <div className="w-full max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200">
            <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200">
              <svg
                className="w-5 h-5 text-slate-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 100-15 7.5 7.5 0 000 15z"
                />
              </svg>
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActiveIdx(0);
                }}
                onKeyDown={onKeyDown}
                placeholder="Search pages, jobs, freelancers…"
                className="flex-1 text-base bg-transparent focus:outline-none placeholder-slate-400"
                aria-label="Search"
              />
              <kbd className="hidden sm:inline-block text-[10px] font-mono text-slate-400 border border-slate-200 rounded px-1.5 py-0.5">
                ESC
              </kbd>
            </div>
            <div
              ref={listRef}
              className="max-h-[60vh] overflow-y-auto"
              role="listbox"
            >
              {filtered.length === 0 ? (
                <div className="px-4 py-10 text-center text-slate-500 text-sm">
                  No results for "{query}"
                </div>
              ) : (
                grouped.map(([cat, items]) => (
                  <div key={cat}>
                    <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wide text-slate-400 font-semibold">
                      {cat}
                    </div>
                    {items.map((item) => {
                      const isActive = item._idx === activeIdx;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          data-cmd-idx={item._idx}
                          role="option"
                          aria-selected={isActive}
                          onMouseEnter={() => setActiveIdx(item._idx)}
                          onClick={() => activate(item)}
                          className={`w-full text-left px-4 py-2.5 flex items-center gap-3 transition-colors focus-visible:outline-none ${
                            isActive ? 'bg-indigo/10' : 'hover:bg-slate-50'
                          }`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-slate-900 font-medium truncate">
                              {item.label}
                            </div>
                            {item.subtitle && (
                              <div className="text-xs text-slate-500 truncate">
                                {item.subtitle}
                              </div>
                            )}
                          </div>
                          {isActive && (
                            <kbd className="hidden sm:inline-block text-[10px] font-mono text-slate-400">
                              ↵
                            </kbd>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
            <div className="flex items-center justify-between px-4 py-2 border-t border-slate-100 bg-slate-50 text-[11px] text-slate-500">
              <span>
                <kbd className="font-mono text-slate-400">↑↓</kbd> navigate ·{' '}
                <kbd className="font-mono text-slate-400">↵</kbd> select
              </span>
              <span>
                <kbd className="font-mono text-slate-400">⌘K</kbd> to toggle
              </span>
            </div>
          </div>
        </div>
      )}
    </CommandContext.Provider>
  );
};

/**
 * Small trigger button — place anywhere to open the palette. Keybinding
 * remains the canonical way to open it.
 */
export const CommandPaletteTrigger = ({ className = '' }) => {
  const { open } = useCommandPalette();
  return (
    <button
      type="button"
      onClick={open}
      aria-label="Open command palette"
      className={`hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 text-slate-500 text-xs hover:bg-slate-50 hover:border-slate-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo transition-colors ${className}`}
    >
      <svg
        className="w-4 h-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 100-15 7.5 7.5 0 000 15z"
        />
      </svg>
      <span>Search…</span>
      <kbd className="font-mono text-[10px] text-slate-400 border border-slate-200 rounded px-1 py-0.5">
        ⌘K
      </kbd>
    </button>
  );
};

export default CommandPaletteProvider;
